import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink } from 'lucide-react';
import analyticsService from '../services/analyticsService';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Trade Like Me', path: '/trade-like-me' },
    { name: 'P&L Dashboard', path: '/dashboard' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Track navigation clicks
  const handleNavigationClick = (navItem, isMobile = false) => {
    analyticsService.trackNavigation(
      location.pathname,
      navItem.path,
      isMobile ? 'mobile_nav_click' : 'desktop_nav_click'
    );
    analyticsService.trackButtonClick(
      navItem.name,
      'navigation',
      { 
        destination: navItem.path,
        navigation_type: isMobile ? 'mobile' : 'desktop',
        from_page: location.pathname
      }
    );
  };

  // Track mobile menu toggle
  const handleMobileMenuToggle = () => {
    setIsOpen(!isOpen);
    analyticsService.trackButtonClick(
      isOpen ? 'Close Mobile Menu' : 'Open Mobile Menu',
      'mobile_menu_toggle',
      { current_page: location.pathname }
    );
  };

  // Track external link clicks
  const handleExternalLinkClick = (linkText, linkUrl, isMobile = false) => {
    analyticsService.trackLinkClick(linkText, linkUrl, 'external');
    analyticsService.trackButtonClick(
      linkText,
      'navigation',
      { 
        link_type: 'external',
        destination: linkUrl,
        navigation_type: isMobile ? 'mobile' : 'desktop'
      }
    );
  };

  // Track logo click
  const handleLogoClick = () => {
    analyticsService.trackButtonClick(
      'Logo',
      'navigation',
      { 
        destination: '/',
        from_page: location.pathname
      }
    );
  };

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3"
            onClick={handleLogoClick}
          >
            <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">K</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Kunal Agarwal</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => handleNavigationClick(item, false)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* CTA Button */}
              <a
                href="https://calendly.com/kunal-twoandhalfcents/30min"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleExternalLinkClick('Book Call', 'https://calendly.com/kunal-twoandhalfcents/30min', false)}
                className="ml-4 inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Book Call
                <ExternalLink className="ml-1 w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-200"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => {
                    handleNavigationClick(item, true);
                    setIsOpen(false);
                  }}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile CTA */}
              <a
                href="https://calendly.com/kunal-twoandhalfcents/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full mt-4 px-3 py-2 bg-gray-900 text-white text-center font-medium rounded-lg hover:bg-gray-800 transition-all duration-200"
                onClick={() => {
                  handleExternalLinkClick('Book a Call', 'https://calendly.com/kunal-twoandhalfcents/30min', true);
                  setIsOpen(false);
                }}
              >
                Book a Call
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;